import { accessibleFill, contrastRatio } from './contrast.js';
import { hexToRgb, rgbToHex } from './convert.js';
import { mixOklab } from './oklab.js';

/**
 * The primary CTA fill, derived from the tenant's ACCENT.
 *
 * 🔴 THIS LIVES IN `@auctisync/color` SO THERE IS EXACTLY ONE OF IT. It was in
 * `@auctisync/tokens`, reachable only by the native apps, while the website
 * restated the rule by hand in `bidder-elevated.css` — and the two had already
 * drifted apart: the app shipped the accent-derived CTA with a measured edge
 * while the web was still painting `--cta-bg: var(--brand-primary)` with
 * `--cta-edge: transparent`. One tenant, two different primary buttons.
 *
 * Nothing here is web- or native-specific: it is colour arithmetic. `theme.ts`
 * calls it for the app, `brandingToTokens` calls it for the website, and a
 * parity test asserts they agree.
 *
 * WHY ACCENT AND NOT PRIMARY (decided 2026-08, shipped):
 *  - the old primary-derived rule failed AA for a whole CLASS of palettes, not
 *    just DTA's mid-grey — mutation testing surfaced a saturated purple at 3.07;
 *  - there are TWO contrast questions and the old rule answered only one.
 *    Label-on-button needs AA 4.5; BUTTON-ON-CANVAS needs WCAG non-text 3.0,
 *    and nobody had measured it — AuctiSync's own navy CTA sat at 2.10 against
 *    the dark canvas.
 *
 * So the edge is not decoration. It is the second contrast requirement, and it
 * is why `--cta-edge` is always a real colour and never `transparent`: a pale
 * accent on white is 1.15, and without an edge that button has no shape.
 */

/** WCAG 2.1 non-text contrast: a UI boundary must reach 3:1 against its ground. */
const UI_BOUNDARY = 3;

export interface CtaFill {
  bg: string;
  fg: string;
  edge: string;
}

/**
 * @param accentHex      The tenant's accent, as authored.
 * @param surfaceElevHex What the button actually sits on — the ELEVATED
 *                       surface, not the page background behind it. Getting
 *                       this wrong is how a button passes a measurement it
 *                       never has to satisfy in practice.
 */
export function ctaFromAccent(
  accentHex: string,
  surfaceElevHex: string,
  mode: 'light' | 'dark',
): CtaFill {
  const dark = mode === 'dark';
  const filled = accessibleFill(hexToRgb(accentHex), mode);
  const bg = rgbToHex(filled.fill);
  const fg = rgbToHex(filled.fg);

  // Walk the edge away from the fill until the button has a visible boundary.
  // Direction follows the mode: darken on light, lighten on dark, so the edge
  // reads as a rim rather than a glow.
  const away = dark ? '#ffffff' : '#000000';
  let edge = mixOklab(bg, 24, away);
  for (let step = 32; step <= 88 && contrastRatio(edge, surfaceElevHex) < UI_BOUNDARY; step += 8) {
    edge = mixOklab(bg, step, away);
  }

  return { bg, fg, edge };
}

/**
 * The elevated surface a CTA sits on, per mode.
 *
 * Duplicated from the theme's neutral table on purpose: this package cannot
 * depend on `@auctisync/tokens` (the dependency runs the other way), and a CTA
 * derivation that took the surface as an untyped argument would let a caller
 * measure against the wrong ground. A parity test pins these to the theme's.
 */
export const CTA_SURFACE_ELEV: Record<'light' | 'dark', string> = {
  light: '#ffffff',
  dark: '#191b21',
};
